// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { FeatureCollection } from 'geojson';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import DatePicker from 'wdna-date-selector';

import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { setFilterData } from 'wdna-components/lib/mainfilter/actions';
import { parseDates, getBoundsOfDatesList } from 'wdna-components/lib/helpers/date';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { ContextType, FilterContext } from '../../../../../types';
import { typeSensorVariable, TypeSensorVariableListDO } from '../../../../configuration/requests';
import { sensor, SensorDO, SensorListDO } from '../../../../sensors/requests';

interface Props {
  rowId?: number;
}

interface State {
  sensorArray: SensorListDO[];
  typeVariableArray: TypeSensorVariableListDO[];
  typeSensorId: string;

  date: Date[];
  flatpickrMode: string;
  groupDateTime: string;

  id?: number;
  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;

  sensorInfo?: SensorDO;
  variables: any[];
}

export default class Filter extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private firstRender: boolean;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.firstRender = true;

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 2);
    const dEnd: Date = new Date();

    this.state = {
      sensorArray: [],
      typeVariableArray: [],
      typeSensorId: '3', // el 3 corresponde a ThermHygro Mobile

      date: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',

      variables: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public componentDidUpdate(): void {
    const { firstRender } = this;
    if (firstRender) {
      this.firstRender = false;
      this.exportInitialData();
    }
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    const filter: Record<string, any> = {};
    filter.status = 'ON_AIR';
    filter.typeSensorId = this.state.typeSensorId;

    this.loadingStart();
    sensor<SensorListDO[]>('list', undefined, filter)
      ?.then((resp: SensorListDO[]) => {
        if (resp && resp.length > 0) {
          this.setState(
            {
              id: resp[0].id,
              sensorArray: resp,
            }
            // this.loadVariables
          );
        } else {
          messageWarning('Warning', 'There are not mobile sensors in the system');
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const twoDaysBefore: Date = moment().add(-2, 'days').toDate();
    return [twoDaysBefore, today];
  };

  public submit = async (): Promise<void> => {
    const idSensor: number | undefined = this.state.id || this.props.rowId;
    if (!idSensor) {
      messageError('Error', 'Please select a device');
      return;
    }

    const req: Record<string, any> = {
      values: this.state.variables,
    };
    if (this.state.dateIni) {
      req.from = this.state.dateIni;
      if (this.state.timeIni) req.from = `${req.from}T${this.state.timeIni}:00.000Z`;
    }
    if (this.state.dateEnd) {
      req.to = this.state.dateEnd;
      if (this.state.timeEnd) req.to = `${req.to}T${this.state.timeEnd}:00.000Z`;
    }
    // TODO: static to type 2 make dinamic to future mobile sensors
    req.lon = '4';
    req.lat = '5';
    req.values = [2, 3];
    req.alias = ['Temperature', 'Humidity'];

    if (!this.chkFilter(req)) return;

    this.loadingStart();
    try {
      const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor)) || undefined;
      const mapData: FeatureCollection | undefined = await sensor<FeatureCollection>('gis_tracking', String(idSensor), req);

      if (this.mounted && sensorInfo) {
        this.readingsLoad(sensorInfo, mapData);
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public toTimestamp = (strDate: string): number => {
    const datum: number = Date.parse(strDate);
    return datum / 1000;
  };

  public loadVariables = async (): Promise<void> => {
    const idSensor: number | undefined = this.state.id || this.props.rowId;

    this.loadingStart();
    try {
      const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor)) || undefined;
      if (sensorInfo) {
        const typeSensorId: number = sensorInfo.typeSensor.id;
        const typeVariableArray: TypeSensorVariableListDO[] = await typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId }) || [];
        if (this.mounted) {
          this.setState({
            sensorInfo,
            typeVariableArray,
            variables: ['2', '3'],
          });
        }
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public readingsLoad = (sensorInfo: SensorDO, mapData?: FeatureCollection): void => {
    this.context?.updateParent({
      update: window.getStr(),
      loading: true,
      mapData,
      sensorInfo,
    });
  };

  public chkFilter = (data: Record<string, any>): boolean => {
    const { from, to } = data;
    if (!from || !to) {
      messageError('Error', 'Please select valid dates for the interval');
      return false;
    }
    return true;
  };

  public loadingStart = (): void => {
    this.context?.updateParent({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public exportInitialData = (): void => {
    this.setState({}, () => {
      this.notifyFilterChange();
      this.exportMainFilter();
    });
  };

  public exportMainFilter = (): void => {
    const mainFilter: any = this.getFilterState();

    this.context?.updateParent({
      mainFilter,
    });

    window.store.dispatch(setFilterData(mainFilter));
  };

  public notifyFilterChange = (): void => {
    this.context?.updateParent({
      onFilterChange: this.getFilterState(),
    });
  };

  public getFilterState = (): void => {
    const { state } = this;

    const mainFilter: any = _.cloneDeep(state);

    const { groupDateTime, flatpickrMode, date } = mainFilter;

    const options: any = {
      granularity: groupDateTime,
      mode: flatpickrMode,
    };

    mainFilter.arrFilterDate = parseDates(date, options);

    if (flatpickrMode === 'multiple') {
      const boundDates: any = getBoundsOfDatesList(mainFilter.arrFilterDate);
      mainFilter.arrFilterDate = boundDates;
    }

    /* clean the filter */
    return mainFilter;
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: checked,
      }));
    } else {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  public handleChangeDevice = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }), () => {
      /* this.loadVariables(); */
    });
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  public handleFilterBtnClick = (): void => {
    this.exportMainFilter();
    this.submit().catch(() => { /* pass */ });
  };

  public addValue = (id: string): void => {
    if (this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    const selection: any[] = !variables ? [] : variables.slice();
    selection.push(id);
    this.setState({
      variables: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: string): void => {
    if (!this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    if (!variables) return;
    const selection: any[] = variables.filter((itemId: any) => itemId !== id);
    this.setState({
      variables: selection,
    });
  };

  public isValueSelected = (id: string): boolean => {
    const { variables } = this.state || {};
    if (!variables) return false;
    return variables.includes(id);
  };

  public handleDatePickerChanges = (config: any): void => {
    const date: Date[] = config.getDates();
    this.setState({
      date,
      flatpickrMode: config.getMode(),
    });
    const preDataIni: Date = date[0];
    const newDataIni: string = moment(preDataIni).format('YYYY-MM-DD');
    this.setState({ dateIni: newDataIni });
    const preDataEnd: Date = date[1];
    if (this.state.flatpickrMode === 'range') {
      const newDataEnd: string = moment(preDataEnd).format('YYYY-MM-DD');
      this.setState({ dateEnd: newDataEnd });
    } else {
      const copyData: string = moment(newDataIni).add(1, 'days').format('YYYY-MM-DD');
      this.setState({ dateEnd: copyData });
    }
  };

  public render(): JSX.Element {
    const { sensorArray, typeVariableArray } = this.state;
    const { date, flatpickrMode, groupDateTime } = this.state;

    const showMainFilter: boolean = (this.props.rowId == null);

    return (
      <div className="main-filters">
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="id"
              data={sensorArray.map((item: SensorListDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.description}`,
                  title: `${item.code} - ${item.name}`,
                };
              })}
              value={this.state.id}
              options={{
                placeholder: 'Select Sensor',
              }}
              onChange={this.handleChangeDevice}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <UiValidate>
          {false && (
            <div className="sideFilter" style={{ marginBottom: '15px' }}>
              <Select2
                multiple
                name="variables"
                data={typeVariableArray.map((item: TypeSensorVariableListDO, idx: number): DataSelect2 => {
                  return {
                    id: idx,
                    text: `${item.variableName}`,
                    title: `${item.variableName} [D${idx}] [${item.variableUnit}]`,
                  };
                })}
                value={this.state.variables}
                options={{
                  placeholder: 'Select variables',
                }}
                onSelect={this.handleChangeMultiple}
                onUnselect={this.handleChangeMultiple}
                style={{ width: '99%' }}
                required
              />
            </div>
          )}
          <DatePicker
            icon
            hours={[]}
            dates={date}
            showChangeModes
            mode={flatpickrMode}
            granularity={groupDateTime}
            iconClass="wdna-ico-icon-calendar"
            handleChanges={this.handleDatePickerChanges}
          />
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={this.handleFilterBtnClick}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </UiValidate>
      </div>
    );
  }
}
